import { Check, CircleSlash } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  claveDe,
  cola as colaDeCuenta,
  colorDeBanco,
  comoVaElVoucher,
  diaMes,
  dinero,
  type Voucher,
} from "./comun";

// ===========================================================================
// LOS VOUCHERS DEL EXPEDIENTE, uno debajo de otro.
//
// En columna y no en rejilla porque no se comparan entre sí: se atienden en
// orden y hay que ver de un vistazo cuál falta. Con la rejilla de miniaturas
// que tenía la ficha del archivo, un expediente de once imágenes no era un
// expediente sino un montón — lo dijo contabilidad con esas palabras.
//
// Cada tarjeta enseña las cuatro cosas con las que se busca el abono: cuánto,
// cuándo, en qué cuenta cayó y por qué medio. La miniatura va al lado y no
// arriba: se usa para reconocer el papel, no para leerlo.
// ===========================================================================

export function RailDeVouchers({
  vouchers,
  elegido,
  alElegir,
}: {
  vouchers: Voucher[];
  /** La clave del PAGO elegido, `recorte|pagoN`: una foto puede traer dos. */
  elegido: string | null;
  alElegir: (clave: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {vouchers.map((v, i) => {
        const estado = comoVaElVoucher(v);
        const seleccionado = claveDe(v) === elegido;
        return (
          <button
            key={claveDe(v)}
            type="button"
            onClick={() => alElegir(claveDe(v))}
            aria-current={seleccionado}
            className={cn(
              "relative flex w-full gap-3 overflow-hidden rounded-lg border bg-card p-3 pl-3.5 text-left shadow-sm transition-colors",
              seleccionado
                ? "border-cyan-500 ring-3 ring-cyan-500/15"
                : "hover:bg-muted/40",
            )}
          >
            {/* La cinta del estado, que es lo que se busca al recorrer la
                columna: verde lo resuelto, ámbar lo que falta. */}
            <span
              className={cn(
                "absolute inset-y-0 left-0 w-[3px]",
                estado.tono === "ok"
                  ? "bg-emerald-600"
                  : estado.tono === "neutro"
                    ? "bg-slate-400"
                    : seleccionado
                      ? "bg-cyan-500"
                      : "bg-amber-500",
              )}
            />

            <img
              src={`/imagen/${v.recorte}`}
              alt=""
              loading="lazy"
              className="size-14 shrink-0 rounded border bg-muted object-cover"
            />

            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="font-bold text-[15.5px] tabular-nums tracking-tight">
                  {dinero(v.importe, v.moneda)}
                </span>
                <span className="ml-auto shrink-0 text-[10.5px] text-muted-foreground">
                  {i + 1} de {vouchers.length}
                </span>
              </div>

              <div className="truncate text-[11.5px] text-muted-foreground">
                {diaMes(v.fechaPago)}
                {v.hora ? ` · ${v.hora}` : ""}
                {v.banco ? ` · ${v.banco}` : ""}
              </div>

              <div className="mt-1.5 flex flex-wrap gap-1">
                {/* Cuando la foto trae más de un voucher hay que decirlo: dos
                    tarjetas con la misma imagen y el mismo importe se leen
                    como un duplicado, y son dos pagos distintos. */}
                {(v.dePagos ?? 1) > 1 ? (
                  <span className="rounded border border-cyan-500/40 bg-cyan-500/10 px-1.5 py-0.5 text-[10.5px] text-cyan-700 dark:text-cyan-300">
                    pago {v.pagoN} de {v.dePagos} en esta foto
                  </span>
                ) : null}
                {v.medio ? <Chip>{v.medio}</Chip> : null}
                {v.origenImagen ? <Chip>{v.origenImagen}</Chip> : null}
                {v.interbancaria ? <Chip acento>Interbancaria</Chip> : null}
              </div>

              {/* A QUÉ CUENTA DEL GRUPO APUNTA. Es lo que decide dónde se
                  buscan los candidatos, así que se ve siempre — también
                  cuando no se pudo resolver, que es el caso que obliga a
                  buscar en las cuatro cuentas. */}
              <div className="mt-1.5 flex items-center gap-1.5 text-[11.5px]">
                <span
                  className="size-2 shrink-0 rounded-[2px]"
                  style={{
                    background: v.cuentaDelGrupo
                      ? colorDeBanco(v.cuentaDelGrupo.banco)
                      : "var(--border)",
                  }}
                />
                <span className="truncate text-muted-foreground">
                  {v.cuentaDelGrupo
                    ? `${colaDeCuenta(v.cuentaDelGrupo.numero)} · ${v.cuentaDelGrupo.banco} ${v.cuentaDelGrupo.moneda === "USD" ? "dólares" : "soles"}`
                    : v.beneficiario
                      ? `Sin cuenta · a nombre de ${v.beneficiario}`
                      : "Sin cuenta de destino"}
                </span>
              </div>

              <div className="mt-2">
                {v.enlace ? (
                  <Pastilla tono="ok">
                    <Check className="size-3" />
                    Atado · {diaMes(v.enlace.fecha)}
                    {v.enlace.operacion ? ` op ${v.enlace.operacion}` : ""}
                  </Pastilla>
                ) : v.sinAbono ? (
                  <Pastilla tono="neutro">
                    <CircleSlash className="size-3" />
                    No está en ningún extracto
                  </Pastilla>
                ) : (
                  <Pastilla tono="warn">Sin atar</Pastilla>
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Chip({
  children,
  acento,
}: {
  children: React.ReactNode;
  acento?: boolean;
}) {
  return (
    <span
      className={cn(
        "rounded-full border px-2 py-0.5 font-medium text-[10px] uppercase tracking-wide",
        acento
          ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-700 dark:text-cyan-400"
          : "text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}

function Pastilla({
  tono,
  children,
}: {
  tono: "ok" | "warn" | "neutro";
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-semibold text-[10.5px] uppercase tracking-wide",
        tono === "ok"
          ? "border-emerald-600/40 bg-emerald-600/10 text-emerald-700 dark:text-emerald-400"
          : tono === "warn"
            ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
            : "bg-muted text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}
